import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';

// Comprehensive font list organized by family
const FONT_FAMILIES = [
    {
        family: 'Academy Engraved LET',
        fonts: ['AcademyEngravedLetPlain'],
    },
    {
        family: 'Al Bayan',
        fonts: ['AlBayan', 'AlBayan-Bold'],
    },
    {
        family: 'Al Nile',
        fonts: ['AlNile', 'AlNile-Bold'],
    },
    {
        family: 'Al Tarikh',
        fonts: ['AlTarikh'],
    },
    {
        family: 'American Typewriter',
        fonts: [
            'AmericanTypewriter-Condensed',
            'AmericanTypewriter',
            'AmericanTypewriter-CondensedLight',
            'AmericanTypewriter-Semibold',
            'AmericanTypewriter-Bold',
            'AmericanTypewriter-Light',
            'AmericanTypewriter-CondensedBold',
        ],
    },
    {
        family: 'Andale Mono',
        fonts: ['AndaleMono'],
    },
    {
        family: 'Apple Braille',
        fonts: [
            'AppleBraille',
            'AppleBraille-Outline8Dot',
            'AppleBraille-Pinpoint6Dot',
            'AppleBraille-Pinpoint8Dot',
            'AppleBraille-Outline6Dot',
        ],
    },
    {
        family: 'Apple Chancery',
        fonts: ['Apple-Chancery'],
    },
    {
        family: 'Apple Color Emoji',
        fonts: ['AppleColorEmoji'],
    },
    {
        family: 'Apple SD Gothic Neo',
        fonts: [
            'AppleSDGothicNeo-SemiBold',
            'AppleSDGothicNeo-ExtraBold',
            'AppleSDGothicNeo-Regular',
            'AppleSDGothicNeo-Heavy',
            'AppleSDGothicNeo-Bold',
            'AppleSDGothicNeo-UltraLight',
            'AppleSDGothicNeo-Medium',
            'AppleSDGothicNeo-Thin',
            'AppleSDGothicNeo-Light',
        ],
    },
    {
        family: 'Apple Symbols',
        fonts: ['AppleSymbols'],
    },
    {
        family: 'AppleGothic',
        fonts: ['AppleGothic'],
    },
    {
        family: 'AppleMyungjo',
        fonts: ['AppleMyungjo'],
    },
    {
        family: 'Arial',
        fonts: [
            'ArialMT',
            'Arial-BoldMT',
            'Arial-BoldItalicMT',
            'Arial-ItalicMT',
        ],
    },
    {
        family: 'Arial Black',
        fonts: ['Arial-Black'],
    },
    {
        family: 'Arial Hebrew',
        fonts: ['ArialHebrew-Bold', 'ArialHebrew', 'ArialHebrew-Light'],
    },
    {
        family: 'Arial Hebrew Scholar',
        fonts: [
            'ArialHebrewScholar-Bold',
            'ArialHebrewScholar',
            'ArialHebrewScholar-Light',
        ],
    },
    {
        family: 'Arial Narrow',
        fonts: [
            'ArialNarrow-BoldItalic',
            'ArialNarrow-Italic',
            'ArialNarrow-Bold',
            'ArialNarrow',
        ],
    },
    {
        family: 'Arial Rounded MT Bold',
        fonts: ['ArialRoundedMTBold'],
    },
    {
        family: 'Arial Unicode MS',
        fonts: ['ArialUnicodeMS'],
    },
    {
        family: 'August Roma Script',
        fonts: ['AugustRomaScript'],
    },
    {
        family: 'Avenir',
        fonts: [
            'Avenir-Light',
            'Avenir-Medium',
            'Avenir-Roman',
            'Avenir-LightOblique',
            'Avenir-HeavyOblique',
            'Avenir-Book',
            'Avenir-BookOblique',
            'Avenir-MediumOblique',
            'Avenir-Black',
            'Avenir-BlackOblique',
            'Avenir-Heavy',
            'Avenir-Oblique',
        ],
    },
    {
        family: 'Avenir Next',
        fonts: [
            'AvenirNext-Regular',
            'AvenirNext-UltraLight',
            'AvenirNext-Heavy',
            'AvenirNext-MediumItalic',
            'AvenirNext-HeavyItalic',
            'AvenirNext-Medium',
            'AvenirNext-DemiBoldItalic',
            'AvenirNext-BoldItalic',
            'AvenirNext-Italic',
            'AvenirNext-UltraLightItalic',
            'AvenirNext-DemiBold',
            'AvenirNext-Bold',
        ],
    },
    {
        family: 'Avenir Next Condensed',
        fonts: [
            'AvenirNextCondensed-DemiBold',
            'AvenirNextCondensed-HeavyItalic',
            'AvenirNextCondensed-BoldItalic',
            'AvenirNextCondensed-Bold',
            'AvenirNextCondensed-Regular',
            'AvenirNextCondensed-DemiBoldItalic',
            'AvenirNextCondensed-Italic',
            'AvenirNextCondensed-Heavy',
            'AvenirNextCondensed-UltraLightItalic',
            'AvenirNextCondensed-UltraLight',
            'AvenirNextCondensed-Medium',
            'AvenirNextCondensed-MediumItalic',
        ],
    },
    {
        family: 'Ayuthaya',
        fonts: ['Ayuthaya'],
    },
    {
        family: 'Baghdad',
        fonts: ['Baghdad'],
    },
    {
        family: 'Bangla MN',
        fonts: ['BanglaMN', 'BanglaMN-Bold'],
    },
    {
        family: 'Bangla Sangam MN',
        fonts: ['BanglaSangamMN', 'BanglaSangamMN-Bold'],
    },
    {
        family: 'Baskerville',
        fonts: [
            'Baskerville-Italic',
            'Baskerville-SemiBold',
            'Baskerville-SemiBoldItalic',
            'Baskerville-Bold',
            'Baskerville-BoldItalic',
            'Baskerville',
        ],
    },
    {
        family: 'Bebas Kai',
        fonts: ['BebasKai'],
    },
    {
        family: 'Bebas Neue',
        fonts: ['BebasNeue-Regular'],
    },
    {
        family: 'Beirut',
        fonts: ['Beirut'],
    },
    {
        family: 'Big Caslon',
        fonts: ['BigCaslon-Medium'],
    },
    {
        family: 'Blenny',
        fonts: ['Blenny-Black'],
    },
    {
        family: 'Bodoni 72',
        fonts: [
            'BodoniSvtyTwoITCTT-Bold',
            'BodoniSvtyTwoITCTT-Book',
            'BodoniSvtyTwoITCTT-BookIta',
        ],
    },
    {
        family: 'Bodoni 72 Oldstyle',
        fonts: [
            'BodoniSvtyTwoOSITCTT-Bold',
            'BodoniSvtyTwoOSITCTT-Book',
            'BodoniSvtyTwoOSITCTT-BookIt',
        ],
    },
    {
        family: 'Bodoni 72 Smallcaps',
        fonts: ['BodoniSvtyTwoSCITCTT-Book'],
    },
    {
        family: 'Bodoni Ornaments',
        fonts: ['BodoniOrnamentsITCTT'],
    },
    {
        family: 'Bradley Hand',
        fonts: ['BradleyHandITCTT-Bold'],
    },
    {
        family: 'Brady Bunch Remastered',
        fonts: ['BradyBunchRemastered'],
    },
    {
        family: 'Brush Script MT',
        fonts: ['BrushScriptMT'],
    },
    {
        family: 'Chalkboard',
        fonts: ['Chalkboard-Bold', 'Chalkboard'],
    },
    {
        family: 'Chalkboard SE',
        fonts: [
            'ChalkboardSE-Light',
            'ChalkboardSE-Bold',
            'ChalkboardSE-Regular',
        ],
    },
    {
        family: 'Chalkduster',
        fonts: ['Chalkduster'],
    },
    {
        family: 'Charter',
        fonts: [
            'Charter-Black',
            'Charter-Italic',
            'Charter-Bold',
            'Charter-BoldItalic',
            'Charter-BlackItalic',
            'Charter-Roman',
        ],
    },
    {
        family: 'Cochin',
        fonts: ['Cochin-Italic', 'Cochin', 'Cochin-Bold', 'Cochin-BoldItalic'],
    },
    {
        family: 'Comic Sans MS',
        fonts: ['ComicSansMS', 'ComicSansMS-Bold'],
    },
    {
        family: 'Copperplate',
        fonts: ['Copperplate-Bold', 'Copperplate', 'Copperplate-Light'],
    },
    {
        family: 'Corsiva Hebrew',
        fonts: ['CorsivaHebrew', 'CorsivaHebrew-Bold'],
    },
    {
        family: 'Courier New',
        fonts: [
            'CourierNewPS-BoldItalicMT',
            'CourierNewPS-BoldMT',
            'CourierNewPSMT',
            'CourierNewPS-ItalicMT',
        ],
    },
    {
        family: 'Damascus',
        fonts: [
            'Damascus',
            'DamascusLight',
            'DamascusMedium',
            'DamascusSemiBold',
            'DamascusBold',
        ],
    },
    {
        family: 'DecoType Naskh',
        fonts: ['DecoTypeNaskh'],
    },
    {
        family: 'DecoType Nastaleeq Urdu',
        fonts: ['DecoTypeNastaleeqUrdu-Bold', 'DecoTypeNastaleeqUrdu-Regular'],
    },
    {
        family: 'Delio',
        fonts: ['Delio'],
    },
    {
        family: 'Delio-trial',
        fonts: ['Deliotrial'],
    },
    {
        family: 'Devanagari MT',
        fonts: ['DevanagariMT-Bold', 'DevanagariMT'],
    },
    {
        family: 'Devanagari Sangam MN',
        fonts: ['DevanagariSangamMN-Bold', 'DevanagariSangamMN'],
    },
    {
        family: 'Didot',
        fonts: ['Didot', 'Didot-Bold', 'Didot-Italic'],
    },
    {
        family: 'DIN Alternate',
        fonts: ['DINAlternate-Bold'],
    },
    {
        family: 'DIN Condensed',
        fonts: ['DINCondensed-Bold'],
    },
    {
        family: 'Diwan Kufi',
        fonts: ['DiwanKufi'],
    },
    {
        family: 'Diwan Thuluth',
        fonts: ['DiwanThuluth'],
    },
    {
        family: 'Euphemia UCAS',
        fonts: ['EuphemiaUCAS-Bold', 'EuphemiaUCAS-Italic', 'EuphemiaUCAS'],
    },
    {
        family: 'Farah',
        fonts: ['Farah'],
    },
    {
        family: 'Farisi',
        fonts: ['Farisi'],
    },
    {
        family: 'Funkydori',
        fonts: ['Funkydori-Bold'],
    },
    {
        family: 'Futura',
        fonts: [
            'Futura-Bold',
            'Futura-Medium',
            'Futura-MediumItalic',
            'Futura-CondensedExtraBold',
            'Futura-CondensedMedium',
        ],
    },
    {
        family: 'Futura Std',
        fonts: ['FuturaStd-Medium'],
    },
    {
        family: 'Galvji',
        fonts: [
            'Galvji',
            'Galvji-BoldOblique',
            'Galvji-Oblique',
            'Galvji-Bold',
        ],
    },
    {
        family: 'GB18030 Bitmap',
        fonts: ['GB18030Bitmap'],
    },
    {
        family: 'Geeza Pro',
        fonts: ['GeezaPro-Bold', 'GeezaPro'],
    },
    {
        family: 'Geneva',
        fonts: ['Geneva'],
    },
    {
        family: 'Geometr231 Hv BT',
        fonts: ['Geometric231BT-HeavyC'],
    },
    {
        family: 'Georgia',
        fonts: [
            'Georgia-Bold',
            'Georgia-BoldItalic',
            'Georgia',
            'Georgia-Italic',
        ],
    },
    {
        family: 'Gill Sans',
        fonts: [
            'GillSans-Light',
            'GillSans-Bold',
            'GillSans-BoldItalic',
            'GillSans-Italic',
            'GillSans-UltraBold',
            'GillSans',
            'GillSans-LightItalic',
            'GillSans-SemiBold',
            'GillSans-SemiBoldItalic',
        ],
    },
    {
        family: 'Gofar',
        fonts: ['Gofar-Script'],
    },
    {
        family: 'Gotham',
        fonts: ['Gotham-Medium'],
    },
    {
        family: 'Grantha Sangam MN',
        fonts: ['GranthaSangamMN-Regular', 'GranthaSangamMN-Bold'],
    },
    {
        family: 'Gujarati MT',
        fonts: ['GujaratiMT', 'GujaratiMT-Bold'],
    },
    {
        family: 'Gujarati Sangam MN',
        fonts: ['GujaratiSangamMN-Bold', 'GujaratiSangamMN'],
    },
    {
        family: 'Gurmukhi MN',
        fonts: ['GurmukhiMN-Bold', 'GurmukhiMN'],
    },
    {
        family: 'Gurmukhi MT',
        fonts: ['MonotypeGurmukhi'],
    },
    {
        family: 'Gurmukhi Sangam MN',
        fonts: ['GurmukhiSangamMN', 'GurmukhiSangamMN-Bold'],
    },
    {
        family: 'Heiti SC',
        fonts: ['STHeitiSC-Medium', 'STHeitiSC-Light'],
    },
    {
        family: 'Heiti TC',
        fonts: ['STHeitiTC-Medium', 'STHeitiTC-Light'],
    },
    {
        family: 'Helvetica',
        fonts: [
            'Helvetica-Bold',
            'Helvetica-BoldOblique',
            'Helvetica-Oblique',
            'Helvetica-Light',
            'Helvetica',
            'Helvetica-LightOblique',
        ],
    },
    {
        family: 'Helvetica Neue',
        fonts: [
            'HelveticaNeue-Light',
            'HelveticaNeue-Italic',
            'HelveticaNeue-Bold',
            'HelveticaNeue-MediumItalic',
            'HelveticaNeue-CondensedBlack',
            'HelveticaNeue-ThinItalic',
            'HelveticaNeue-BoldItalic',
            'HelveticaNeue-UltraLightItalic',
            'HelveticaNeue-LightItalic',
            'HelveticaNeue-UltraLight',
            'HelveticaNeue-Medium',
            'HelveticaNeue-Thin',
            'HelveticaNeue',
            'HelveticaNeue-CondensedBold',
        ],
    },
    {
        family: 'Herculanum',
        fonts: ['Herculanum'],
    },
    {
        family: 'Hiragino Maru Gothic ProN',
        fonts: ['HiraMaruProN-W4'],
    },
    {
        family: 'Hiragino Mincho ProN',
        fonts: ['HiraMinProN-W3', 'HiraMinProN-W6'],
    },
    {
        family: 'Hiragino Sans',
        fonts: [
            'HiraginoSans-W2',
            'HiraginoSans-W5',
            'HiraginoSans-W8',
            'HiraginoSans-W1',
            'HiraginoSans-W4',
            'HiraginoSans-W7',
            'HiraginoSans-W0',
            'HiraginoSans-W3',
            'HiraginoSans-W6',
            'HiraginoSans-W9',
        ],
    },
    {
        family: 'Hiragino Sans GB',
        fonts: ['HiraginoSansGB-W3', 'HiraginoSansGB-W6'],
    },
    {
        family: 'Hoefler Text',
        fonts: [
            'HoeflerText-Italic',
            'HoeflerText-Black',
            'HoeflerText-BlackItalic',
            'HoeflerText-Regular',
            'HoeflerText-Ornaments',
        ],
    },
    {
        family: 'IBM Plex Mono',
        fonts: ['IBMPlexMono-Bold', 'IBMPlexMono-Regular'],
    },
    {
        family: 'Impact',
        fonts: ['Impact'],
    },
    {
        family: 'InaiMathi',
        fonts: ['InaiMathi', 'InaiMathi-Bold'],
    },
    {
        family: 'IndeedSans',
        fonts: [
            'IndeedSans-ExtraBoldItalic',
            'IndeedSans-Light',
            'IndeedSans-Bold',
            'IndeedSans-Italic',
            'IndeedSans-Regular',
            'IndeedSans-LightItalic',
            'IndeedSans-MediumItalic',
            'IndeedSans-ExtraBold',
            'IndeedSans-Medium',
            'IndeedSans-BoldItalic',
        ],
    },
    {
        family: 'Instagram Sans',
        fonts: [
            'InstagramSans-Regular',
            'InstagramSans-Medium',
            'InstagramSans-Bold',
            'InstagramSans-Light',
        ],
    },
    {
        family: 'Instagram Sans Condensed',
        fonts: [
            'InstagramSansCondensed-Medium',
            'InstagramSansCondensed-Regular',
            'InstagramSansCondensed-Light',
            'InstagramSansCondensed-Bold',
        ],
    },
    {
        family: 'Instagram Sans Headline',
        fonts: ['InstagramSansHeadline-Bold', 'InstagramSansHeadline-Regular'],
    },
    {
        family: 'Instagram Sans Script',
        fonts: ['InstagramSansScript-Regular', 'InstagramSansScript-Bold'],
    },
    {
        family: 'ITF Devanagari',
        fonts: [
            'ITFDevanagari-Light',
            'ITFDevanagari-Bold',
            'ITFDevanagari-Book',
            'ITFDevanagari-Medium',
            'ITFDevanagari-Demi',
        ],
    },
    {
        family: 'ITF Devanagari Marathi',
        fonts: [
            'ITFDevanagariMarathi-Bold',
            'ITFDevanagariMarathi-Demi',
            'ITFDevanagariMarathi-Book',
            'ITFDevanagariMarathi-Light',
            'ITFDevanagariMarathi-Medium',
        ],
    },
    {
        family: 'Kailasa',
        fonts: ['Kailasa', 'Kailasa-Bold'],
    },
    {
        family: 'Kaiti SC',
        fonts: ['STKaitiSC-Black', 'STKaitiSC-Bold', 'STKaitiSC-Regular'],
    },
    {
        family: 'Kaiti TC',
        fonts: ['STKaitiTC-Regular', 'STKaitiTC-Bold', 'STKaitiTC-Black'],
    },
    {
        family: 'Kannada MN',
        fonts: ['KannadaMN-Bold', 'KannadaMN'],
    },
    {
        family: 'Kannada Sangam MN',
        fonts: ['KannadaSangamMN', 'KannadaSangamMN-Bold'],
    },
    {
        family: 'Kefa',
        fonts: ['Kefa-Bold', 'Kefa-Regular'],
    },
    {
        family: 'Khmer MN',
        fonts: ['KhmerMN', 'KhmerMN-Bold'],
    },
    {
        family: 'Khmer Sangam MN',
        fonts: ['KhmerSangamMN'],
    },
    {
        family: 'Knockout',
        fonts: [
            'Knockout-HTF47-Bantamweight',
            'Knockout-HTF92-UltmtCruiserwt',
            'Knockout-30JuniorWelterwt',
            'Knockout-HTF48-Featherweight',
            'Knockout-49Liteweight',
            'Knockout-50Welterweight',
        ],
    },
    {
        family: 'Kohinoor Bangla',
        fonts: [
            'KohinoorBangla-Medium',
            'KohinoorBangla-Regular',
            'KohinoorBangla-Bold',
            'KohinoorBangla-Semibold',
            'KohinoorBangla-Light',
        ],
    },
    {
        family: 'Kohinoor Devanagari',
        fonts: [
            'KohinoorDevanagari-Regular',
            'KohinoorDevanagari-Light',
            'KohinoorDevanagari-Medium',
            'KohinoorDevanagari-Semibold',
            'KohinoorDevanagari-Bold',
        ],
    },
    {
        family: 'Kohinoor Gujarati',
        fonts: [
            'KohinoorGujarati-Medium',
            'KohinoorGujarati-Semibold',
            'KohinoorGujarati-Bold',
            'KohinoorGujarati-Regular',
            'KohinoorGujarati-Light',
        ],
    },
    {
        family: 'Kohinoor Telugu',
        fonts: [
            'KohinoorTelugu-Light',
            'KohinoorTelugu-Bold',
            'KohinoorTelugu-Medium',
            'KohinoorTelugu-Semibold',
            'KohinoorTelugu-Regular',
        ],
    },
    {
        family: 'Kokonor',
        fonts: ['Kokonor'],
    },
    {
        family: 'Krungthep',
        fonts: ['Krungthep'],
    },
    {
        family: 'KufiStandardGK',
        fonts: ['KufiStandardGK'],
    },
    {
        family: 'Lao MN',
        fonts: ['LaoMN', 'LaoMN-Bold'],
    },
    {
        family: 'Lao Sangam MN',
        fonts: ['LaoSangamMN'],
    },
    {
        family: 'Lato',
        fonts: ['Lato-Regular'],
    },
    {
        family: 'League Gothic',
        fonts: ['LeagueGothic-Regular'],
    },
    {
        family: 'Limelight',
        fonts: ['Limelight-Regular'],
    },
    {
        family: 'Lucida Grande',
        fonts: ['LucidaGrande-Bold', 'LucidaGrande'],
    },
    {
        family: 'Luminari',
        fonts: ['Luminari-Regular'],
    },
    {
        family: 'Malayalam MN',
        fonts: ['MalayalamMN', 'MalayalamMN-Bold'],
    },
    {
        family: 'Malayalam Sangam MN',
        fonts: ['MalayalamSangamMN', 'MalayalamSangamMN-Bold'],
    },
    {
        family: 'Margo Script',
        fonts: ['MargoScript-Regular'],
    },
    {
        family: 'Marker Felt',
        fonts: ['MarkerFelt-Thin', 'MarkerFelt-Wide'],
    },
    {
        family: 'Means',
        fonts: ['Means-Regular'],
    },
    {
        family: 'Menlo',
        fonts: [
            'Menlo-Italic',
            'Menlo-BoldItalic',
            'Menlo-Regular',
            'Menlo-Bold',
        ],
    },
    {
        family: 'Microsoft Sans Serif',
        fonts: ['MicrosoftSansSerif'],
    },
    {
        family: 'Milkshake Script',
        fonts: ['MilkshakeScript-Regular'],
    },
    {
        family: 'Milkshake Script Slant',
        fonts: ['MilkshakeScriptSlant-Regular'],
    },
    {
        family: 'Milkshake Script Thin',
        fonts: ['MilkshakeScriptThin-Regular'],
    },
    {
        family: 'Milkshake Script Thin Slant',
        fonts: ['MilkshakeScriptThinSlant-Regular'],
    },
    {
        family: 'Mishafi',
        fonts: ['DiwanMishafi'],
    },
    {
        family: 'Mishafi Gold',
        fonts: ['DiwanMishafiGold'],
    },
    {
        family: 'Moderat',
        fonts: ['Moderat-Regular', 'Moderat-Medium'],
    },
    {
        family: 'Monaco',
        fonts: ['Monaco'],
    },
    {
        family: 'Montserrat',
        fonts: [
            'Montserrat-ExtraLight',
            'Montserrat-Light',
            'Montserrat-LightItalic',
            'Montserrat-SemiBold',
            'Montserrat-Bold',
            'Montserrat-ExtraBold',
            'Montserrat-Medium',
            'Montserrat-Regular',
        ],
    },
    {
        family: 'Mrs Eaves',
        fonts: ['MrsEavesAllPetiteCapsOT', 'MrsEavesAllSmallCapsOT'],
    },
    {
        family: 'Mshtakan',
        fonts: [
            'Mshtakan',
            'MshtakanBoldOblique',
            'MshtakanOblique',
            'MshtakanBold',
        ],
    },
    {
        family: 'Mukta Mahee',
        fonts: [
            'MuktaMahee-Light',
            'MuktaMahee-Medium',
            'MuktaMahee-Bold',
            'MuktaMahee-Regular',
            'MuktaMahee-ExtraLight',
            'MuktaMahee-SemiBold',
            'MuktaMahee-ExtraBold',
        ],
    },
    {
        family: 'Muna',
        fonts: ['Muna', 'MunaBold', 'MunaBlack'],
    },
    {
        family: 'Myanmar MN',
        fonts: ['MyanmarMN', 'MyanmarMN-Bold'],
    },
    {
        family: 'Myanmar Sangam MN',
        fonts: ['MyanmarSangamMN-Bold', 'MyanmarSangamMN'],
    },
    {
        family: 'Nadeem',
        fonts: ['Nadeem'],
    },
    {
        family: 'New Peninim MT',
        fonts: [
            'NewPeninimMT-Bold',
            'NewPeninimMT-BoldInclined',
            'NewPeninimMT',
            'NewPeninimMT-Inclined',
        ],
    },
    {
        family: 'New Stencil tfb',
        fonts: ['NewStenciltfb'],
    },
    {
        family: 'Noteworthy',
        fonts: ['Noteworthy-Light', 'Noteworthy-Bold'],
    },
    {
        family: 'Noto Nastaliq Urdu',
        fonts: ['NotoNastaliqUrdu', 'NotoNastaliqUrdu-Bold'],
    },
    {
        family: 'Noto Sans Batak',
        fonts: ['NotoSansBatak-Regular'],
    },
    {
        family: 'Noto Sans Kannada',
        fonts: [
            'NotoSansKannada-Thin',
            'NotoSansKannada-ExtraBold',
            'NotoSansKannada-Regular',
            'NotoSansKannada-SemiBold',
            'NotoSansKannada-Black',
            'NotoSansKannada-Light',
            'NotoSansKannada-Medium',
            'NotoSansKannada-Bold',
            'NotoSansKannada-ExtraLight',
        ],
    },
    {
        family: 'Noto Sans Myanmar',
        fonts: [
            'NotoSansMyanmar-ExtraBold',
            'NotoSansMyanmar-Regular',
            'NotoSansMyanmar-SemiBold',
            'NotoSansMyanmar-Bold',
            'NotoSansMyanmar-Medium',
            'NotoSansMyanmar-Black',
            'NotoSansMyanmar-Light',
            'NotoSansMyanmar-Thin',
            'NotoSansMyanmar-ExtraLight',
        ],
    },
    {
        family: 'Noto Sans NKo',
        fonts: ['NotoSansNKo-Regular'],
    },
    {
        family: 'Noto Sans Oriya',
        fonts: ['NotoSansOriya', 'NotoSansOriya-Bold'],
    },
    {
        family: 'Noto Sans Syriac',
        fonts: [
            'NotoSansSyriac-Regular_Black',
            'NotoSansSyriac-Regular_ExtraBold',
            'NotoSansSyriac-Regular_Light',
            'NotoSansSyriac-Regular_ExtraLight',
            'NotoSansSyriac-Regular_SemiBold',
            'NotoSansSyriac-Regular_Bold',
            'NotoSansSyriac-Regular_Thin',
            'NotoSansSyriac-Regular',
            'NotoSansSyriac-Regular_Medium',
        ],
    },
    {
        family: 'Noto Sans Tagalog',
        fonts: ['NotoSansTagalog-Regular'],
    },
    {
        family: 'Noto Serif Myanmar',
        fonts: [
            'NotoSerifMyanmar-SemiBold',
            'NotoSerifMyanmar-Bold',
            'NotoSerifMyanmar-Black',
            'NotoSerifMyanmar-Regular',
            'NotoSerifMyanmar-Thin',
            'NotoSerifMyanmar-ExtraBold',
            'NotoSerifMyanmar-ExtraLight',
            'NotoSerifMyanmar-Light',
            'NotoSerifMyanmar-Medium',
        ],
    },
    {
        family: 'Optima',
        fonts: [
            'Optima-ExtraBlack',
            'Optima-Bold',
            'Optima-Regular',
            'Optima-BoldItalic',
            'Optima-Italic',
        ],
    },
    {
        family: 'Oriya MN',
        fonts: ['OriyaMN', 'OriyaMN-Bold'],
    },
    {
        family: 'Oriya Sangam MN',
        fonts: ['OriyaSangamMN-Bold', 'OriyaSangamMN'],
    },
    {
        family: 'Palatino',
        fonts: [
            'Palatino-Bold',
            'Palatino-BoldItalic',
            'Palatino-Roman',
            'Palatino-Italic',
        ],
    },
    {
        family: 'Papyrus',
        fonts: ['Papyrus', 'Papyrus-Condensed'],
    },
    {
        family: 'Party LET',
        fonts: ['PartyLetPlain'],
    },
    {
        family: 'Phosphate',
        fonts: ['Phosphate-Solid', 'Phosphate-Inline'],
    },
    {
        family: 'PingFang HK',
        fonts: [
            'PingFangHK-Thin',
            'PingFangHK-Ultralight',
            'PingFangHK-Medium',
            'PingFangHK-Semibold',
            'PingFangHK-Light',
            'PingFangHK-Regular',
        ],
    },
    {
        family: 'PingFang MO',
        fonts: [
            'PingFangMO-Light',
            'PingFangMO-Ultralight',
            'PingFangMO-Thin',
            'PingFangMO-Regular',
            'PingFangMO-Medium',
            'PingFangMO-Semibold',
        ],
    },
    {
        family: 'PingFang SC',
        fonts: [
            'PingFangSC-Medium',
            'PingFangSC-Regular',
            'PingFangSC-Semibold',
            'PingFangSC-Thin',
            'PingFangSC-Light',
            'PingFangSC-Ultralight',
        ],
    },
    {
        family: 'PingFang TC',
        fonts: [
            'PingFangTC-Semibold',
            'PingFangTC-Medium',
            'PingFangTC-Regular',
            'PingFangTC-Ultralight',
            'PingFangTC-Light',
            'PingFangTC-Thin',
        ],
    },
    {
        family: 'Plantagenet Cherokee',
        fonts: ['PlantagenetCherokee'],
    },
    {
        family: 'Poppins',
        fonts: ['Poppins-Bold', 'Poppins-SemiBold'],
    },
    {
        family: 'Proxima Nova',
        fonts: ['ProximaNova-Extrabld'],
    },
    {
        family: 'PT Mono',
        fonts: ['PTMono-Regular', 'PTMono-Bold'],
    },
    {
        family: 'PT Sans',
        fonts: [
            'PTSans-Regular',
            'PTSans-Italic',
            'PTSans-BoldItalic',
            'PTSans-Bold',
        ],
    },
    {
        family: 'PT Sans Caption',
        fonts: ['PTSans-CaptionBold', 'PTSans-Caption'],
    },
    {
        family: 'PT Sans Narrow',
        fonts: ['PTSans-Narrow', 'PTSans-NarrowBold'],
    },
    {
        family: 'PT Serif',
        fonts: [
            'PTSerif-Regular',
            'PTSerif-Bold',
            'PTSerif-BoldItalic',
            'PTSerif-Italic',
        ],
    },
    {
        family: 'PT Serif Caption',
        fonts: ['PTSerif-CaptionItalic', 'PTSerif-Caption'],
    },
    {
        family: 'Raanana',
        fonts: ['Raanana', 'RaananaBold'],
    },
    {
        family: 'Recoleta Alt',
        fonts: ['RecoletaAlt-Black'],
    },
    {
        family: 'Roboto',
        fonts: [
            'Roboto-Regular',
            'Roboto-Italic',
            'Roboto-BoldItalic',
            'Roboto-Black',
            'Roboto-MediumItalic',
            'Roboto-Medium',
            'Roboto-Bold',
            'Roboto-BlackItalic',
        ],
    },
    {
        family: 'Roboto Condensed',
        fonts: ['RobotoCondensed-Regular', 'RobotoCondensed-Bold'],
    },
    {
        family: 'Rockwell',
        fonts: [
            'Rockwell-Regular',
            'Rockwell-Bold',
            'Rockwell-Italic',
            'Rockwell-BoldItalic',
        ],
    },
    {
        family: 'Sana',
        fonts: ['Sana'],
    },
    {
        family: 'Sathu',
        fonts: ['Sathu'],
    },
    {
        family: 'Savoye LET',
        fonts: ['SavoyeLetPlain'],
    },
    {
        family: 'SF Compact',
        fonts: [
            'SFCompact-Bold',
            'SFCompact-RegularItalic',
            'SFCompact-BoldItalic',
            'SFCompact-Thin',
            'SFCompact-Medium',
            'SFCompact-ThinItalic',
            'SFCompact-MediumItalic',
            'SFCompact-Ultralight',
            'SFCompact-Regular',
            'SFCompact-Light',
            'SFCompact-Heavy',
            'SFCompact-SemiboldItalic',
            'SFCompact-UltralightItalic',
            'SFCompact-Black',
            'SFCompact-LightItalic',
            'SFCompact-HeavyItalic',
            'SFCompact-BlackItalic',
            'SFCompact-Semibold',
        ],
    },
    {
        family: 'SF Compact Display',
        fonts: [
            'SFCompactDisplay-Thin',
            'SFCompactDisplay-Heavy',
            'SFCompactDisplay-Black',
            'SFCompactDisplay-Regular',
            'SFCompactDisplay-Medium',
            'SFCompactDisplay-Bold',
            'SFCompactDisplay-Ultralight',
            'SFCompactDisplay-Light',
            'SFCompactDisplay-Semibold',
        ],
    },
    {
        family: 'SF Compact Rounded',
        fonts: [
            'SFCompactRounded-Black',
            'SFCompactRounded-Regular',
            'SFCompactRounded-Medium',
            'SFCompactRounded-Bold',
            'SFCompactRounded-Semibold',
            'SFCompactRounded-Thin',
            'SFCompactRounded-Ultralight',
            'SFCompactRounded-Light',
            'SFCompactRounded-Heavy',
        ],
    },
    {
        family: 'SF Compact Text',
        fonts: [
            'SFCompactText-Medium',
            'SFCompactText-Light',
            'SFCompactText-BoldItalic',
            'SFCompactText-BlackItalic',
            'SFCompactText-UltralightItalic',
            'SFCompactText-Italic',
            'SFCompactText-HeavyItalic',
            'SFCompactText-Ultralight',
            'SFCompactText-LightItalic',
            'SFCompactText-SemiboldItalic',
            'SFCompactText-Thin',
            'SFCompactText-Semibold',
            'SFCompactText-Heavy',
            'SFCompactText-Black',
            'SFCompactText-Bold',
            'SFCompactText-Regular',
            'SFCompactText-ThinItalic',
            'SFCompactText-MediumItalic',
        ],
    },
    {
        family: 'SF Pro',
        fonts: [
            'SFPro-CondensedLight',
            'SFPro-CondensedSemibold',
            'SFPro-CompressedBlack',
            'SFPro-CondensedMedium',
            'SFPro-SemiboldItalic',
            'SFPro-CompressedMedium',
            'SFPro-ExpandedLight',
            'SFPro-Thin',
            'SFPro-LightItalic',
            'SFPro-CompressedLight',
            'SFPro-CondensedUltralight',
            'SFPro-Bold',
            'SFPro-CompressedSemibold',
            'SFPro-ExpandedRegular',
            'SFPro-ExpandedBlack',
            'SFPro-CondensedRegular',
            'SFPro-CondensedHeavy',
            'SFPro-ThinItalic',
            'SFPro-CompressedRegular',
            'SFPro-CompressedUltralight',
            'SFPro-Heavy',
            'SFPro-Light',
            'SFPro-BlackItalic',
            'SFPro-ExpandedUltralight',
            'SFPro-ExpandedThin',
            'SFPro-CompressedHeavy',
            'SFPro-CondensedThin',
            'SFPro-MediumItalic',
            'SFPro-RegularItalic',
            'SFPro-CompressedBold',
            'SFPro-UltralightItalic',
            'SFPro-Semibold',
            'SFPro-BoldItalic',
            'SFPro-HeavyItalic',
            'SFPro-ExpandedHeavy',
            'SFPro-Medium',
            'SFPro-ExpandedMedium',
            'SFPro-CondensedBold',
            'SFPro-CompressedThin',
            'SFPro-CondensedBlack',
            'SFPro-Regular',
            'SFPro-ExpandedBold',
            'SFPro-ExpandedSemibold',
            'SFPro-Black',
            'SFPro-Ultralight',
        ],
    },
    {
        family: 'SF Pro Display',
        fonts: [
            'SFProDisplay-MediumItalic',
            'SFProDisplay-Semibold',
            'SFProDisplay-UltralightItalic',
            'SFProDisplay-Heavy',
            'SFProDisplay-HeavyItalic',
            'SFProDisplay-Bold',
            'SFProDisplay-Regular',
            'SFProDisplay-Ultralight',
            'SFProDisplay-LightItalic',
            'SFProDisplay-Medium',
            'SFProDisplay-BoldItalic',
            'SFProDisplay-Black',
            'SFProDisplay-Light',
            'SFProDisplay-RegularItalic',
            'SFProDisplay-BlackItalic',
            'SFProDisplay-ThinItalic',
            'SFProDisplay-Thin',
            'SFProDisplay-SemiboldItalic',
        ],
    },
    {
        family: 'SF Pro Rounded',
        fonts: [
            'SFProRounded-Ultralight',
            'SFProRounded-Bold',
            'SFProRounded-Black',
            'SFProRounded-Medium',
            'SFProRounded-Thin',
            'SFProRounded-Semibold',
            'SFProRounded-Regular',
            'SFProRounded-Light',
            'SFProRounded-Heavy',
        ],
    },
    {
        family: 'SF Pro Text',
        fonts: [
            'SFProText-HeavyItalic',
            'SFProText-ThinItalic',
            'SFProText-RegularItalic',
            'SFProText-Ultralight',
            'SFProText-SemiboldItalic',
            'SFProText-Semibold',
            'SFProText-Black',
            'SFProText-BlackItalic',
            'SFProText-Light',
            'SFProText-Regular',
            'SFProText-Medium',
            'SFProText-LightItalic',
            'SFProText-MediumItalic',
            'SFProText-Bold',
            'SFProText-BoldItalic',
            'SFProText-Thin',
            'SFProText-UltralightItalic',
            'SFProText-Heavy',
        ],
    },
    {
        family: 'Shree Devanagari 714',
        fonts: [
            'ShreeDev0714-BoldItalic',
            'ShreeDev0714',
            'ShreeDev0714-Bold',
            'ShreeDev0714-Italic',
        ],
    },
    {
        family: 'Shrikhand',
        fonts: ['Shrikhand-Regular'],
    },
    {
        family: 'SignPainter',
        fonts: ['SignPainter-HouseScript', 'SignPainter-HouseScriptSemibold'],
    },
    {
        family: 'Silom',
        fonts: ['Silom'],
    },
    {
        family: 'Sinhala MN',
        fonts: ['SinhalaMN-Bold', 'SinhalaMN'],
    },
    {
        family: 'Sinhala Sangam MN',
        fonts: ['SinhalaSangamMN-Bold', 'SinhalaSangamMN'],
    },
    {
        family: 'Skia',
        fonts: [
            'Skia-Regular_Bold',
            'Skia-Regular_Black-Condensed',
            'Skia-Regular_Light',
            'Skia-Regular_Black',
            'Skia-Regular_Condensed',
            'Skia-Regular_Black-Extended',
            'Skia-Regular_Light-Condensed',
            'Skia-Regular_Extended',
            'Skia-Regular_Light-Extended',
            'Skia-Regular',
        ],
    },
    {
        family: 'Snell Roundhand',
        fonts: [
            'SnellRoundhand',
            'SnellRoundhand-Black',
            'SnellRoundhand-Bold',
        ],
    },
    {
        family: 'Songti SC',
        fonts: [
            'STSongti-SC-Light',
            'STSongti-SC-Black',
            'STSongti-SC-Regular',
            'STSongti-SC-Bold',
        ],
    },
    {
        family: 'Songti TC',
        fonts: ['STSongti-TC-Regular', 'STSongti-TC-Bold', 'STSongti-TC-Light'],
    },
    {
        family: 'STIX Two Math',
        fonts: ['STIXTwoMath-Regular'],
    },
    {
        family: 'STIX Two Text',
        fonts: [
            'STIXTwoText_SemiBold',
            'STIXTwoText-Italic_Bold-Italic',
            'STIXTwoText_Medium',
            'STIXTwoText-Italic_Medium-Italic',
            'STIXTwoText-Italic_SemiBold-Italic',
            'STIXTwoText_Bold',
            'STIXTwoText-Italic',
            'STIXTwoText',
        ],
    },
    {
        family: 'STKaiti',
        fonts: ['STKaiti'],
    },
    {
        family: 'STSong',
        fonts: ['STSong'],
    },
    {
        family: 'Sukhumvit Set',
        fonts: [
            'SukhumvitSet-Light',
            'SukhumvitSet-Medium',
            'SukhumvitSet-SemiBold',
            'SukhumvitSet-Text',
            'SukhumvitSet-Bold',
            'SukhumvitSet-Thin',
        ],
    },
    {
        family: 'Symbol',
        fonts: ['Symbol'],
    },
    {
        family: 'Tahoma',
        fonts: ['Tahoma', 'Tahoma-Bold'],
    },
    {
        family: 'Tamil MN',
        fonts: ['TamilMN', 'TamilMN-Bold'],
    },
    {
        family: 'Tamil Sangam MN',
        fonts: ['TamilSangamMN', 'TamilSangamMN-Bold'],
    },
    {
        family: 'Telugu MN',
        fonts: ['TeluguMN-Bold', 'TeluguMN'],
    },
    {
        family: 'Telugu Sangam MN',
        fonts: ['TeluguSangamMN', 'TeluguSangamMN-Bold'],
    },
    {
        family: 'Thonburi',
        fonts: ['Thonburi-Light', 'Thonburi', 'Thonburi-Bold'],
    },
    {
        family: 'Times New Roman',
        fonts: [
            'TimesNewRomanPS-BoldMT',
            'TimesNewRomanPS-ItalicMT',
            'TimesNewRomanPSMT',
            'TimesNewRomanPS-BoldItalicMT',
        ],
    },
    {
        family: 'Trajan Pro 3',
        fonts: ['TrajanPro3-Semibold'],
    },
    {
        family: 'Trattatello',
        fonts: ['Trattatello'],
    },
    {
        family: 'Trebuchet MS',
        fonts: [
            'TrebuchetMS',
            'TrebuchetMS-Italic',
            'TrebuchetMS-Bold',
            'Trebuchet-BoldItalic',
        ],
    },
    {
        family: 'Verdana',
        fonts: [
            'Verdana-Italic',
            'Verdana',
            'Verdana-Bold',
            'Verdana-BoldItalic',
        ],
    },
    {
        family: 'Waseem',
        fonts: ['WaseemLight', 'Waseem'],
    },
    {
        family: 'Webdings',
        fonts: ['Webdings'],
    },
    {
        family: 'Wingdings',
        fonts: ['Wingdings-Regular'],
    },
    {
        family: 'Wingdings 2',
        fonts: ['Wingdings2'],
    },
    {
        family: 'Wingdings 3',
        fonts: ['Wingdings3'],
    },
    {
        family: 'Zapf Dingbats',
        fonts: ['ZapfDingbatsITC'],
    },
    {
        family: 'Zapfino',
        fonts: ['Zapfino'],
    },
];

// Create a flat list of all fonts for searching
const ALL_FONTS = FONT_FAMILIES.flatMap((family) =>
    family.fonts.map((font) => ({
        name: font,
        family: family.family,
        displayName: `${font} (${family.family})`,
    })),
);

interface FontDropdownProps {
    value?: string;
    onChange: (fontName: string) => void;
    placeholder?: string;
    className?: string;
}

export default function FontDropdown({
    value,
    onChange,
    placeholder = 'Select a font...',
    className = '',
}: FontDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Filter fonts based on search term
    const filteredFonts = useMemo(() => {
        if (!searchTerm.trim()) return ALL_FONTS;

        const term = searchTerm.toLowerCase();
        return ALL_FONTS.filter(
            (font) =>
                font.name.toLowerCase().includes(term) ||
                font.family.toLowerCase().includes(term),
        );
    }, [searchTerm]);

    // Find the selected font info
    const selectedFont = ALL_FONTS.find((font) => font.name === value);

    // Calculate dropdown position with edge detection
    const updateDropdownPosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownHeight = 320; // max-h-80 = 320px
            const dropdownWidth = Math.max(rect.width, 300); // minWidth: 300px
            const padding = 8; // padding from viewport edge

            let top = rect.bottom + window.scrollY;
            let left = rect.left + window.scrollX;

            // Check if dropdown would go off the bottom of the viewport
            if (rect.bottom + dropdownHeight > window.innerHeight) {
                // Position above the trigger instead
                top = rect.top + window.scrollY - dropdownHeight;

                // If it still doesn't fit above, position at the top of viewport
                if (top < window.scrollY + padding) {
                    top = window.scrollY + padding;
                }
            }

            // Check if dropdown would go off the right edge
            if (rect.left + dropdownWidth > window.innerWidth) {
                // Align to the right edge of the trigger
                left = rect.right + window.scrollX - dropdownWidth;

                // If it still doesn't fit, align to viewport edge
                if (left < window.scrollX + padding) {
                    left = window.scrollX + padding;
                }
            }

            // Ensure dropdown doesn't go off the left edge
            if (left < window.scrollX + padding) {
                left = window.scrollX + padding;
            }

            setDropdownPosition({
                top,
                left,
                width: rect.width,
            });
        }
    };

    // Update position when opening dropdown
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            // Update position on scroll/resize
            const handleResize = () => updateDropdownPosition();
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleResize, true);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleResize, true);
            };
        }
    }, [isOpen]);

    const handleSelect = (fontName: string) => {
        onChange(fontName);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            updateDropdownPosition();
        }
    };

    return (
        <div className={cn('relative', className)}>
            {/* Dropdown trigger */}
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                className={cn(
                    'w-full px-3 py-2 text-left bg-background border border-border rounded-md shadow-sm',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    'flex items-center justify-between',
                    'hover:bg-muted/50 transition-colors',
                )}
            >
                <span
                    className={cn(
                        'block truncate text-sm',
                        !selectedFont && 'text-muted-foreground',
                    )}
                    style={
                        selectedFont ? { fontFamily: selectedFont.name } : {}
                    }
                >
                    {selectedFont ? selectedFont.displayName : placeholder}
                </span>
                <ChevronDown
                    className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform',
                        isOpen && 'rotate-180',
                    )}
                />
            </button>

            {/* Portal-based dropdown menu */}
            {isOpen &&
                createPortal(
                    <>
                        {/* Backdrop to close dropdown */}
                        <div
                            className="fixed inset-0 z-40"
                            onClick={() => setIsOpen(false)}
                        />

                        {/* Dropdown menu */}
                        <div
                            className="absolute z-50 bg-background border border-border rounded-md shadow-lg max-h-80 overflow-hidden"
                            style={{
                                top: dropdownPosition.top,
                                left: dropdownPosition.left,
                                width: dropdownPosition.width,
                                minWidth: '300px', // Ensure minimum width for readability
                            }}
                        >
                            {/* Search input */}
                            <div className="p-2 border-b border-border">
                                <div className="flex items-center gap-2 rounded-md border border-input px-3 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                                    <Search className="size-4 shrink-0 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search fonts..."
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                        className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {/* Font list */}
                            <div className="overflow-y-auto max-h-64">
                                {filteredFonts.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                                        No fonts found
                                    </div>
                                ) : (
                                    filteredFonts.map((font) => (
                                        <button
                                            key={font.name}
                                            type="button"
                                            onClick={() =>
                                                handleSelect(font.name)
                                            }
                                            className={cn(
                                                'w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors',
                                                'focus:outline-none focus:bg-muted',
                                                value === font.name &&
                                                    'bg-blue-50 text-blue-700',
                                            )}
                                            style={{ fontFamily: font.name }}
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">
                                                    {font.name}
                                                </span>
                                                <span
                                                    className="text-xs text-muted-foreground"
                                                    style={{
                                                        fontFamily:
                                                            'system-ui, -apple-system, sans-serif',
                                                    }}
                                                >
                                                    {font.family}
                                                </span>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </>,
                    document.body,
                )}
        </div>
    );
}
